import React, { useState, useEffect } from 'react';
import SearchBox from './SearchBox';
import UniversalChat from './UniversalChat';
import { authPost } from '../lib/authFetch';
import './ListView.css';

const EXAMPLE_PROMPTS = [
  "By how recently I've talked to them",
  "Hot, warm, and cold leads",
  "Board members, major donors, and prospects",
  "People I need to follow up with this week",
  "By giving capacity"
];

function ListView({ dataMode, savedLists = [], onSaveList, onSelectList, activeListId = null, onSelectDonor, pendingListData, onListDataConsumed, onAction, conversationHistory, onAddMessage, lastDonorUpdate, onListRefreshed, listRefreshTrigger = 0 }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [groups, setGroups] = useState(null);
  const [organizationPrompt, setOrganizationPrompt] = useState('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [currentListId, setCurrentListId] = useState(null);
  const [draggedPerson, setDraggedPerson] = useState(null);
  const [draggedFromGroup, setDraggedFromGroup] = useState(null);
  const [dropTargetGroup, setDropTargetGroup] = useState(null);
  const [viewMode, setViewMode] = useState('rows'); // 'rows' or 'columns'

  // Handle pending list data from universal chat
  useEffect(() => {
    if (pendingListData) {
      console.log('ListView received pending list data:', pendingListData);
      // Create a new list from the pending data - handle both formats
      let newGroups;
      if (pendingListData.groups && pendingListData.groups.length > 0) {
        // New format with groups array
        newGroups = pendingListData.groups;
      } else if (pendingListData.people) {
        // Old format with just people array
        newGroups = [{ name: pendingListData.name, people: pendingListData.people }];
      } else {
        newGroups = [{ name: pendingListData.name || 'New List', people: [] }];
      }
      const listName = pendingListData.name || 'Created from chat';
      setGroups(newGroups);
      setOrganizationPrompt(listName);
      setCurrentListId(null); // Clear any selected saved list

      // Auto-save the list to the server
      const saveList = async () => {
        try {
          const response = await authPost('/api/lists/save', {
            organizationPrompt: listName,
            groups: newGroups,
            mode: dataMode
          });

          if (response.ok) {
            const data = await response.json();
            setCurrentListId(data.list.id);
            if (onSaveList) {
              onSaveList(data.list);
            }
          }
        } catch (error) {
          console.error('Failed to save list from chat:', error);
        }
      };
      saveList();

      // Consume the data immediately
      if (onListDataConsumed) {
        onListDataConsumed();
      }
    }
  }, [pendingListData, onListDataConsumed, dataMode, onSaveList]);

  // Cycle through examples
  useEffect(() => {
    if (groups) return; // Don't cycle if we have groups

    const interval = setInterval(() => {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setCurrentExampleIndex(prev => (prev + 1) % EXAMPLE_PROMPTS.length);
        setIsAnimatingOut(false);
      }, 200);
    }, 3500);

    return () => clearInterval(interval);
  }, [groups]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    const prompt = searchQuery;
    setOrganizationPrompt(prompt);

    try {
      const response = await authPost('/api/organize-list', {
        query: prompt,
        mode: dataMode
      });

      if (!response.ok) throw new Error('Server error');

      const data = await response.json();
      setGroups(data.groups);

      // Auto-save the new list
      const saveResponse = await authPost('/api/lists/save', {
        organizationPrompt: prompt,
        groups: data.groups,
        mode: dataMode
      });

      if (saveResponse.ok) {
        const saveData = await saveResponse.json();
        setCurrentListId(saveData.list.id);
        if (onSaveList) {
          onSaveList(saveData.list);
        }
      }
    } catch (error) {
      console.error('Failed to organize list:', error);
      // Fallback: create mock groups based on the query
      setGroups([
        { name: 'Group 1', people: ['Sample Person 1', 'Sample Person 2'] },
        { name: 'Group 2', people: ['Sample Person 3'] },
        { name: 'Group 3', people: ['Sample Person 4', 'Sample Person 5'] }
      ]);
    }
    setIsLoading(false);
    setSearchQuery('');
  };

  // eslint-disable-next-line no-unused-vars
  const handleReset = () => {
    setGroups(null);
    setOrganizationPrompt('');
    setCurrentListId(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleSaveList = async () => {
    if (!groups || !organizationPrompt || isSaving) return;

    setIsSaving(true);
    try {
      const response = await authPost('/api/lists/save', {
        organizationPrompt,
        groups,
        mode: dataMode
      });

      if (!response.ok) throw new Error('Failed to save');

      const data = await response.json();
      setCurrentListId(data.list.id);
      if (onSaveList) {
        onSaveList(data.list);
      }
    } catch (error) {
      console.error('Failed to save list:', error);
    }
    setIsSaving(false);
  };

  // Track when we last refreshed to prevent infinite loops
  const [lastRefreshedListId, setLastRefreshedListId] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);

  // Load a saved list when activeListId changes (but not if we have pending data)
  useEffect(() => {
    // Don't load saved list if we have pending data from universal chat
    if (pendingListData) return;

    if (activeListId && savedLists.length > 0) {
      const list = savedLists.find(l => l.id === activeListId);
      if (list) {
        setGroups(list.groups);
        setOrganizationPrompt(list.organizationPrompt);
        setCurrentListId(list.id);

        // Check if we should refresh:
        // - Don't refresh if we just refreshed this same list within the last 2 seconds
        // - Refresh when listRefreshTrigger changes or on initial load
        const now = Date.now();
        const justRefreshed = lastRefreshedListId === activeListId && (now - lastRefreshTime) < 2000;

        if (justRefreshed) {
          console.log('Skipping refresh - just refreshed this list');
          return;
        }

        const shouldRefresh = dataMode === 'real' ||
          (lastDonorUpdate || 0) > (list.lastEvaluated ? new Date(list.lastEvaluated).getTime() : 0);

        if (shouldRefresh) {
          console.log('Refreshing list:', list.name);
          setIsRefreshing(true);
          setLastRefreshedListId(activeListId);
          setLastRefreshTime(now);

          authPost('/api/lists/refresh', {
            listId: list.id,
            mode: dataMode,
            organizationPrompt: list.organizationPrompt,
            listName: list.name,
            currentGroups: list.groups,
            groupId: list.groupId
          }).then(response => {
            if (response.ok) {
              return response.json();
            }
            throw new Error('Refresh failed');
          }).then(data => {
            if (data.success && data.list) {
              setGroups(data.list.groups);
              // Notify parent to update the saved list
              if (onListRefreshed) {
                onListRefreshed(data.list);
              }
            }
          }).catch(error => {
            console.error('Failed to refresh list:', error);
          }).finally(() => {
            setIsRefreshing(false);
          });
        }
      }
    } else if (activeListId === null) {
      // Reset to empty state when activeListId is explicitly set to null
      setGroups(null);
      setOrganizationPrompt('');
      setCurrentListId(null);
      setSearchQuery('');
    }
    // Note: savedLists intentionally excluded to prevent infinite loops when onListRefreshed updates it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeListId, pendingListData, lastDonorUpdate, dataMode, listRefreshTrigger]);

  // Empty state - asking how to organize
  if (!groups) {
    return (
      <div className="list-view empty">
        <div className="list-view-prompt">
          <h2 className="list-view-title">How would you like to organize people?</h2>
          <div className="list-view-search">
            <SearchBox
              placeholder=""
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              expandable={true}
              loading={isLoading}
            />
            {!searchQuery && (
              <div className={`list-view-example ${isAnimatingOut ? 'animating-out' : 'animating-in'}`}>
                {EXAMPLE_PROMPTS[currentExampleIndex]}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Drag and drop handlers
  const handleDragStart = (person, groupIndex) => {
    setDraggedPerson(person);
    setDraggedFromGroup(groupIndex);
  };

  const handleDragEnd = () => {
    setDraggedPerson(null);
    setDraggedFromGroup(null);
    setDropTargetGroup(null);
  };

  const handleDragOver = (e, groupIndex) => {
    e.preventDefault();
    if (groupIndex !== draggedFromGroup) {
      setDropTargetGroup(groupIndex);
    }
  };

  const handleDragLeave = () => {
    setDropTargetGroup(null);
  };

  const handleDrop = (e, targetGroupIndex) => {
    e.preventDefault();
    if (draggedPerson && draggedFromGroup !== null && targetGroupIndex !== draggedFromGroup) {
      // Move person from one group to another
      setGroups(prevGroups => {
        const newGroups = prevGroups.map((group, idx) => ({
          ...group,
          people: [...group.people]
        }));

        // Remove from source group
        newGroups[draggedFromGroup].people = newGroups[draggedFromGroup].people.filter(
          p => p !== draggedPerson
        );

        // Add to target group
        newGroups[targetGroupIndex].people.push(draggedPerson);

        return newGroups;
      });

      // Mark as unsaved
      setCurrentListId(null);
    }

    setDraggedPerson(null);
    setDraggedFromGroup(null);
    setDropTargetGroup(null);
  };

  // Handle refining the list with a new instruction
  // eslint-disable-next-line no-unused-vars
  const handleRefine = async () => {
    if (!searchQuery.trim() || !groups) return;

    setIsLoading(true);
    setCurrentListId(null); // Mark as unsaved since we're changing it

    try {
      const response = await authPost('/api/refine-list', {
        instruction: searchQuery,
        currentGroups: groups,
        originalPrompt: organizationPrompt,
        mode: dataMode
      });

      if (!response.ok) throw new Error('Server error');

      const data = await response.json();
      setGroups(data.groups);
      // Keep the original organization prompt but could append refinements
    } catch (error) {
      console.error('Failed to refine list:', error);
    }
    setIsLoading(false);
    setSearchQuery('');
  };

  // Groups view - showing organized people
  // eslint-disable-next-line no-unused-vars
  const isSaved = currentListId !== null;

  return (
    <div className="list-view has-groups">
      <div className="list-view-header">
        <div className="list-view-chat">
          <UniversalChat
            dataMode={dataMode}
            onAction={onAction}
            conversationHistory={conversationHistory}
            onAddMessage={onAddMessage}
            autoFocus={true}
            alwaysShowSubmit={true}
            hidePreview={true}
          />
        </div>
        <button
          className="list-view-mode-toggle"
          onClick={() => setViewMode(viewMode === 'rows' ? 'columns' : 'rows')}
          title={viewMode === 'rows' ? 'Switch to column view' : 'Switch to row view'}
        >
          {viewMode === 'rows' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="18" rx="1" />
              <rect x="14" y="3" width="7" height="18" rx="1" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="7" rx="1" />
              <rect x="3" y="14" width="18" height="7" rx="1" />
            </svg>
          )}
        </button>
      </div>

      <div className={`list-groups ${viewMode} ${isRefreshing ? 'refreshing' : ''}`}>
        {groups.map((group, index) => (
          <div
            key={index}
            className={`list-group ${dropTargetGroup === index ? 'drop-target' : ''}`}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
          >
            <h3 className="list-group-name">{group.name}</h3>
            <div className="list-group-people">
              {group.people.map((person, personIndex) => (
                <button
                  key={personIndex}
                  className={`list-person ${draggedPerson === person ? 'dragging' : ''}`}
                  onClick={() => onSelectDonor && onSelectDonor(person)}
                  draggable
                  onDragStart={() => handleDragStart(person, index)}
                  onDragEnd={handleDragEnd}
                >
                  {person}
                </button>
              ))}
              {group.people.length === 0 && (
                <span className="list-group-empty">Drop someone here</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ListView;
